import { Link } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';

const FamilyBridgeSection = () => {
  const familyBenefits = [
    'Know when things are steady without asking every day',
    'Stay informed without monitoring or micromanaging',
    'Support independence while protecting the relationship',
    'Reduce worry without increasing pressure'
  ];

  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-primary/5 via-secondary/15 to-accent/10">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Primary Block - For Older Adult */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              The End of the "Just Checking In" Phone Call.
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6 max-w-3xl mx-auto">
              When you're living independently, constant check-ins can feel annoying, even when they come from love. 
              SeniorThrive helps you share steady signals that things are going well, so family can relax and you can stay in charge.
            </p>
            <p className="text-xl font-bold text-foreground">
              You decide what they see. You stay in control.
            </p>
          </div>

          {/* Family Acknowledgment - Nested */}
          <div className="bg-card/80 backdrop-blur-sm rounded-md p-8 md:p-12 shadow-raised">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              For Families: Support Without Hovering
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              If you're supporting a parent or loved one, SeniorThrive gives you calm confidence 
              without constant calls. You see steady signals that things are stable, and you step 
              in only when something changes.
            </p>

            {/* Benefits List - 2 columns on larger screens */}
            <ul className="grid sm:grid-cols-2 gap-4 mb-8">
              {familyBenefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Link to="/home-safety-quiz">
                <Button
                  size="lg"
                  className="text-base px-6 py-5 min-h-[56px] rounded-sm font-bold"
                >
                  Take the Home Safety Check-In
                </Button>
              </Link>
              <Link to="/thrivefamily">
                <Button
                  variant="outline"
                  size="lg"
                  className="text-base px-6 py-5 min-h-[48px] rounded-sm font-bold border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
                >
                  Explore for Families
                </Button>
              </Link>
            </div>
          </div>

          {/* Testimonials */}
          <div className="mt-16">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
              Life at Home, Working Better
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  quote: "My kids worry less now that they see I'm doing fine. It's better for all of us.",
                  name: "Martha",
                  age: 74,
                  context: "Living independently in Florida"
                },
                {
                  quote: "I used to call my mom every morning just to make sure she was okay. Now I glance at her ThriveScore and we actually talk about life instead.",
                  name: "David",
                  age: 48,
                  context: "Son and family caregiver"
                },
                {
                  quote: "The daily check-in takes seconds but gives my daughter peace of mind. I don't feel like I'm being watched.",
                  name: "Robert",
                  age: 79,
                  context: "Retired teacher in Ohio"
                }
              ].map((testimonial, index) => (
                <div 
                  key={index} 
                  className="bg-card rounded-md p-6 shadow-raised flex flex-col"
                >
                  <div className="flex text-orange-400 mb-4 text-sm">★★★★★</div>
                  <blockquote className="text-foreground/90 italic leading-relaxed mb-4 flex-grow">
                    "{testimonial.quote}"
                  </blockquote>
                  <div className="border-t border-border/50 pt-4 mt-auto">
                    <p className="font-bold text-foreground">
                      {testimonial.name}, {testimonial.age}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {testimonial.context}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FamilyBridgeSection;
